import { AxiosError } from 'axios';

/**
 * Normalized API error shape. Backend returns `{ error, message, errors[] }`
 * inconsistently across endpoints — this collapses them so callers and form
 * adapters see a single contract.
 */
export interface ApiError {
  status: number;
  message: string;
  /** Field-level validation errors keyed by form field name. */
  fieldErrors?: Record<string, string>;
  /** Original axios payload, for breadcrumb / debug surfaces. */
  raw?: unknown;
}

interface BackendValidationError {
  field?: string;
  path?: string;
  message?: string;
  msg?: string;
}

interface BackendErrorBody {
  error?: string;
  message?: string;
  errors?: BackendValidationError[] | Record<string, string>;
}

/** Convert any unknown error (axios or otherwise) into a stable ApiError. */
export function toApiError(error: unknown): ApiError {
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 0;
    const body = error.response?.data as BackendErrorBody | undefined;

    const message =
      body?.message ||
      body?.error ||
      error.message ||
      'Request failed';

    const fieldErrors = collectFieldErrors(body?.errors);

    return { status, message, fieldErrors, raw: body };
  }

  if (error instanceof Error) {
    return { status: 0, message: error.message };
  }

  return { status: 0, message: 'Unknown error' };
}

function collectFieldErrors(
  errors: BackendValidationError[] | Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!errors) return undefined;

  if (Array.isArray(errors)) {
    const map: Record<string, string> = {};
    for (const e of errors) {
      const key = e.field ?? e.path;
      const msg = e.message ?? e.msg;
      if (key && msg) map[key] = msg;
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }

  if (typeof errors === 'object') {
    return errors as Record<string, string>;
  }

  return undefined;
}

/**
 * Quick predicates for common HTTP states. Used in interceptors and route
 * guards to decide whether to redirect, refresh, or surface a toast.
 */
export const isUnauthorized = (e: ApiError): boolean => e.status === 401;
export const isForbidden = (e: ApiError): boolean => e.status === 403;
export const isValidation = (e: ApiError): boolean => e.status === 422 || !!e.fieldErrors;
export const isServer = (e: ApiError): boolean => e.status >= 500;
export const isNetwork = (e: ApiError): boolean => e.status === 0;
